import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LayoutDashboard, FolderKanban, Users, Building2, Bell, LogOut, Menu, X, Sun, ChevronDown, BarChart3, FileText, Map, GanttChart, ClipboardCheck, AlertTriangle, FolderOpen, Link2, Award, Activity, Package, DollarSign, Calendar, CloudSun, FileSpreadsheet, TrendingUp } from 'lucide-react';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: null },
  { to: '/projects', icon: FolderKanban, label: 'Proyectos', roles: null },
  { to: '/analytics', icon: BarChart3, label: 'Analytics', roles: null },
  { to: '/reports', icon: FileText, label: 'Reportes', roles: null },
  { to: '/map', icon: Map, label: 'Mapa', roles: null },
  { to: '/gantt', icon: GanttChart, label: 'Gantt', roles: null },
  { to: '/checklists', icon: ClipboardCheck, label: 'Checklists', roles: null },
  { to: '/incidents', icon: AlertTriangle, label: 'Incidencias', roles: null },
  { to: '/documents', icon: FolderOpen, label: 'Documentos', roles: null },
  { to: '/users', icon: Users, label: 'Usuarios', roles: ['superadmin', 'admin_empresa'] },
  { to: '/companies', icon: Building2, label: 'Empresas', roles: ['superadmin'] },
  { to: '/notifications', icon: Bell, label: 'Notificaciones', roles: null },
  { to: '/client-portal', icon: Link2, label: 'Portal Cliente' },
  { to: '/certificates', icon: Award, label: 'Certificados' },
  { to: '/monitoring', icon: Activity, label: 'Monitorización' },
  { to: '/inventory', icon: Package, label: 'Inventario' },
  { to: '/budget', icon: DollarSign, label: 'Presupuesto' },
  { to: '/crews', icon: Calendar, label: 'Cuadrillas' },
  { to: '/weather', icon: CloudSun, label: 'Meteorología' },
  { to: '/audit', icon: FileText, label: 'Auditoría' },
  { to: '/import-export', icon: FileSpreadsheet, label: 'Import/Export' },
  { to: '/profitability', icon: TrendingUp, label: 'Rentabilidad' },
];

const roleLabels = {
  superadmin: 'Super Admin',
  admin_empresa: 'Admin Empresa',
  project_manager: 'Project Manager',
  supervisor: 'Supervisor',
  tecnico: 'Técnico',
  cliente: 'Cliente',
  viewer: 'Visualizador'
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredNav = navItems.filter(item => !item.roles || item.roles.includes(user?.role));

  return (
    <div className="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <Sun size={28} className="logo-icon" />
          <div>
            <h1 className="logo-text">Bitácora Solar</h1>
            <span className="logo-sub" style={{fontSize:'9px',letterSpacing:'2px',textTransform:'uppercase',color:'#94a3b8'}}>by ENERGESTICS</span>
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {filteredNav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info" onClick={() => setUserMenuOpen(!userMenuOpen)}>
            <div className="user-avatar">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.firstName} {user?.lastName}</span>
              <span className="user-role">{roleLabels[user?.role] || user?.role}</span>
            </div>
            <ChevronDown size={16} />
          </div>
          {userMenuOpen && (
            <div className="user-menu">
              {user?.companyName && <div className="user-menu-item company">{user.companyName}</div>}
              <button className="user-menu-item logout" onClick={handleLogout}>
                <LogOut size={16} /> Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="top-bar">
          <button className="menu-toggle" onClick={() => setSidebarOpen(true)}>
            <Menu size={24} />
          </button>
          <div className="top-bar-right">
            <NavLink to="/notifications" className="notif-btn">
              <Bell size={20} />
            </NavLink>
          </div>
        </header>
        <div className="page-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
